import React, {FC, useContext, useState} from 'react';
import {useSelector} from 'react-redux';
import List from '@material-ui/core/List';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import {GuidelineIcon} from '@epic-ui/icons';

import {PDFExportContext} from '../../../components/v2/sections/fortnite-games/export/PDFExportProvider';
import {CSVExportContext} from '../../../components/v2/sections/fortnite-games/export/BulkCSVExportProvider';

import {
    StyledButton,
    StyledInfoIconWrapper,
    StyledListItem,
    StyledPopover
} from './BulkExportButton.styles';
import {RootState} from '../../../index';
import {WidgetState} from '../../../types';
import {Statuses} from './StatusSnackbar';
import Tooltip, {preventBubbling} from '../../../components/common/Tooltip';

const FILE_NAME = `fortnite_creator_data-${new Date().toLocaleDateString()}`;
const CODES = {
    title: 'epic.affiliate.fortnite_games.bulk_export.title',
    pdfTooltip: 'epic.affiliate.fortnite_games.bulk_export.tooltip.pdf',
    csvTooltip: 'epic.affiliate.fortnite_games.bulk_export.tooltip.csv',
    csv: 'epic.affiliate.fortnite_games.widget_download.csv',
    pdf: 'epic.affiliate.fortnite_games.widget_download.pdf'
};

interface BulkExportButtonProps {
    getMessage: (code: string) => string;
}

const BulkExportButton: FC<BulkExportButtonProps> = ({getMessage}: BulkExportButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isDataLoading = useSelector<RootState, WidgetState>(
        state =>
            state.get('games').get('loading') ||
            state.get('activeUsers').get('loading') ||
            state.get('returnPlayers').get('loading') ||
            state.get('lifetimePlays').get('loading')
    );

    const {onDownload} = useContext(CSVExportContext);
    const {onDownload: onDownloadPDF, status} = useContext(PDFExportContext);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <StyledButton
                component="div"
                size="large"
                endIcon={anchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                disabled={isDataLoading || status === Statuses.loading}
                onClick={handleOpen}>
                {getMessage(CODES.title)}
            </StyledButton>
            <StyledPopover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}>
                <List>
                    <StyledListItem
                        onClick={() => {
                            handleClose();
                            onDownload(FILE_NAME);
                        }}>
                        {getMessage(CODES.csv)}
                        <Tooltip
                            placement="right"
                            onClick={preventBubbling}
                            title={getMessage(CODES.csvTooltip)}>
                            <StyledInfoIconWrapper>
                                <GuidelineIcon />
                            </StyledInfoIconWrapper>
                        </Tooltip>
                        <GetAppIcon />
                    </StyledListItem>
                    <StyledListItem
                        onClick={() => {
                            handleClose();
                            onDownloadPDF({name: FILE_NAME});
                        }}>
                        {getMessage(CODES.pdf)}
                        <Tooltip
                            placement="right"
                            onClick={preventBubbling}
                            title={getMessage(CODES.pdfTooltip)}>
                            <StyledInfoIconWrapper>
                                <GuidelineIcon />
                            </StyledInfoIconWrapper>
                        </Tooltip>
                        <GetAppIcon />
                    </StyledListItem>
                </List>
            </StyledPopover>
        </>
    );
};

export default withLocalization(BulkExportButton);
